/**
 * PE Diligence TUI Web - Type Definitions
 */

// Company data types
export interface CompanyData {
  ticker: string
  name: string
  sector: string
  industry: string
  marketCap: number
  description?: string
  quoteType?: 'EQUITY' | 'ETF' | 'MUTUALFUND' | 'PRIVATE'
  isPrivate?: boolean
}

// Tab configuration
export interface Tab {
  id: string
  label: string
  shortcut: string
}

// Financial data
export interface FinancialData {
  year: number
  revenue: number
  netIncome: number
  fcf: number
  assets: number
  liabilities: number
  equity: number
}

// FCF prediction
export interface FCFPrediction {
  year: number
  predicted: number
  lower: number
  upper: number
  confidence: number
}

// Regime classification
export interface RegimeClassification {
  regime: 'growth' | 'stable' | 'transition' | 'distressed'
  confidence: number
  stability: number
  trend: 'improving' | 'stable' | 'declining'
}

// Chat message
export interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  metadata?: Record<string, unknown>
}

// M&A thesis
export interface MAThesis {
  id: string
  name: string
  criteria: {
    minRevenue: number
    maxRevenue: number
    minFcfMargin: number
    sectors: string[]
    regimes: string[]
  }
}

// Report configuration
export interface ReportConfig {
  type: 'quick' | 'full' | 'custom'
  sections: {
    executiveSummary: boolean
    financialAnalysis: boolean
    fcfProjections: boolean
    regimeAnalysis: boolean
    riskAssessment: boolean
    competitorAnalysis: boolean
    managementAssessment: boolean
    valuationAnalysis: boolean
  }
}

// Report section status
export interface ReportSection {
  id: string
  name: string
  status: 'pending' | 'in_progress' | 'completed' | 'failed'
  progress: number
}

// Report
export interface Report {
  id: string
  ticker: string
  companyName: string
  reportType: 'quick' | 'full' | 'custom'
  createdAt: string
  status: 'generating' | 'completed' | 'failed'
  filePath?: string
}

// Status bar state
export interface StatusState {
  message: string
  type: 'info' | 'success' | 'warning' | 'error' | 'loading'
}

// Analysis history entry
export interface AnalysisEntry {
  id: string
  ticker: string
  companyName: string
  analyzedAt: string
  status: 'completed' | 'failed'
}

// Deal pipeline entry
export interface Deal {
  id: string
  ticker: string
  companyName: string
  stage: 'screening' | 'initial_dd' | 'deep_dive' | 'negotiation' | 'closed'
  lastActivity: string
  score?: number
}

// Default tabs configuration
export const DEFAULT_TABS: Tab[] = [
  { id: 'financials', label: 'Financials', shortcut: '1' },
  { id: 'fcf', label: 'FCF', shortcut: '2' },
  { id: 'regime', label: 'Regime', shortcut: '3' },
  { id: 'chat', label: 'Chat', shortcut: '4' },
  { id: 'ma', label: 'M&A', shortcut: '5' },
  { id: 'report', label: 'Report', shortcut: '6' },
  { id: 'dataroom', label: 'Data Room', shortcut: '7' },
  { id: 'history', label: 'History', shortcut: '8' },
]
